import { Devices, Refresh, Check } from '@mui/icons-material'
import {
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Button,
  CircularProgress,
  Box,
} from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'

import type { Transfer } from '@/api/definition'
import { Status } from '@/api/definition'
import Concerns from '@/pages/Concerns'

type DeviceListItemProps = {
  deviceTypeName: string
  serialNr?: string
  ownerLabel?: string
  request?: Transfer
  onRetryClick: React.MouseEventHandler<HTMLButtonElement>
}

const DeviceListItem: React.FC<DeviceListItemProps> = ({
  deviceTypeName,
  serialNr,
  ownerLabel,
  request,
  onRetryClick,
}) => {
  const { t } = useTranslation(['di', 'app', 'common'])

  return (
    <>
      <ListItem
        alignItems="flex-start"
        sx={{
          pr: 8,
          position: 'relative',
          borderBottom: '1px solid #eee',
        }}
      >
        <ListItemAvatar>
          <Avatar>
            <Devices />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <>
              {serialNr}
              {ownerLabel && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ display: 'inline', ml: 1 }}
                  color="text.secondary"
                >
                  {` - ${ownerLabel}`}
                </Typography>
              )}
            </>
          }
          secondary={deviceTypeName}
        />
        {request && (
          <Box
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <RequestStatusIndicator
              status={request.status}
              onRetry={onRetryClick}
            />
          </Box>
        )}
      </ListItem>
      {request?.status === Status.FAILURE && (
        <Concerns
          message={t(request.reason || 'device.error.unknown')}
          concerns={request.concerns}
        />
      )}
    </>
  )
}

type RequestStatusIndicatorProps = {
  status: number
  onRetry: React.MouseEventHandler<HTMLButtonElement>
}
const RequestStatusIndicator: React.FC<RequestStatusIndicatorProps> = ({
  status,
  onRetry,
}) => {
  const { t } = useTranslation(['di', 'app', 'common'])

  switch (status) {
    case Status.IN_PROGRESS:
      return <CircularProgress size={24} />

    case Status.SUCCESS:
      return <Check color="success" />

    case Status.FAILURE:
      return (
        <Button
          size="small"
          variant="contained"
          color="error"
          onClick={onRetry}
          aria-label={t('common:general.retry')}
          sx={{
            minWidth: 0,
            width: 36,
            height: 36,
            borderRadius: '50%',
            p: 0,
            backgroundColor: 'takeoutofservice.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'takeoutofservice.dark',
            },
          }}
        >
          <Refresh fontSize="small" />
        </Button>
      )

    default:
      return null
  }
}

export default DeviceListItem
